import { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, NavLink, generatePath, Outlet } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  ButtonPrimary,
  IconArrowLeft,
  IconLoader,
  Select,
  Table,
  useQueryState,
  ResponseError,
  Popup,
  Textarea,
  Currency,
  formatBalance,
  IconChangeLoClock,
  PermissionEnum,
  formatPhone,
  responseErrorsResolver,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { clearBeneficiaryState } from '../store/slices/beneficiaries/beneficiaries';
import {
  useLazyGetCustomerBeneficiaryQuery,
  useUpdateBeneficiaryStatusMutation,
  useLazyGetCustomerBeneficiaryContractsQuery,
  useLazyGetCustomersBeneficairyDocumentsCategoriesQuery,
  useLazyGetBeneficiaryDocumentFileQuery,
  useValidateCustomerBeneficiaryMutation,
} from '../store/services/customers';
import {
  CustomerType,
  BeneficiaryStatus,
  BeneficiaryContract,
  CustomerStatus,
  BeneficiaryContentRowType,
  Beneficiary,
} from '../types';
import { schema, columns, columnsLabels } from './variables/contracts';
import {
  columns as documentsColumns,
  columnsLabels as documentsColumnsLabels,
} from './variables/documents';
import getStatusName from './helpers/get-status-name';

import ROUTES from '../../../routes';

import cn from './beneficiaries.module.css';

const formatBeneficiaryContractsList = (
  list: BeneficiaryContract[],
  baseCurrency?: Currency,
  currencies?: Currency[],
): BeneficiaryContract[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    const render: Record<string, React.JSX.Element> = {
      sum: (
        <div>
          {result?.sum
            ? `${formatBalance(
                result?.sum,
                currencies?.find(
                  (currency) => currency.id === result?.currencyId,
                ) || baseCurrency,
              )} ${
                result?.currencyId
                  ? currencies?.find(
                      (currency) => currency.id === result?.currencyId,
                    )?.name
                  : ''
              }`
            : '-'}
        </div>
      ),
      description: <div>{result?.description || '-'}</div>,
      status: <div>{getStatusName(result?.status)}</div>,
    };

    result.render = render;

    return result;
  });

  return nextList;
};

const formatBeneficiaryDocumentsList = (
  list: {
    id: string;
    documentCategoryId: string;
    name: string;
    uploadId: string;
    render?: Record<string, React.JSX.Element>;
  }[],
  documentsCategories: { id: string; name: string }[],
  documentIsLoading?: boolean,
  documentIsFetching?: boolean,
  selectedDocumentId?: string,
): {
  id: string;
  documentCategoryId: string;
  name: string;
  uploadId: string;
  render?: Record<string, React.JSX.Element>;
}[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    const render: Record<string, React.JSX.Element> = {
      name: <div>{result.name}</div>,
      documentCategoryId: (
        <div className={cn.documentCategory}>
          {
            documentsCategories?.find(
              (category) => category.id === result.documentCategoryId,
            )?.name
          }
          {(documentIsLoading || documentIsFetching) &&
            selectedDocumentId === result.id && (
              <IconLoader
                width={20}
                height={20}
                className={cn.downloadDocumentLoader}
              />
            )}
        </div>
      ),
    };

    result.render = render;

    return result;
  });

  return nextList;
};

const BeneficiaryContentRow = ({
  name,
  value,
  error,
}: BeneficiaryContentRowType) => (
  <>
    <div className={cn.beneficiaryContentRow}>
      <div className={cn.beneficiaryContentRowTitle}>{name}</div>
      <div className={cn.beneficiaryContentRowValue}>{value}</div>
    </div>
    {error && <div className={cn.beneficiaryContentRowError}>{error}</div>}
  </>
);

const BeneficiaryPage = ({ type }: { type: CustomerType }) => {
  const [beneficiaryStatus, setBeneficiaryStatus] = useState<
    BeneficiaryStatus | undefined
  >(undefined);
  const [updateStatusError, setUpdateStatusError] = useState('');
  const [rejectPopupVisible, setRejectPopupVisible] = useState(false);
  const [rejectComment, setRejectComment] = useState('');
  const [rejectCommentError, setRejectCommentError] = useState('');
  const [selectedDocumentId, setSelectedDocumentId] = useState('');
  const [globalValidateError, setGlobalValidateError] = useState('');

  const { customerId, beneficiaryId } = useParams();

  const {
    customerBeneficiaries: {
      beneficiary,
      beneficiaryContracts,
      documentsCategories,
    },
    customersDictionaries: {
      baseCurrency,
      beneficiarySettings,
      currenciesList,
      activePermissions,
      countries,
    },
    individualCustomers: {
      info: {
        general: { baseInformation: individualBaseInformation },
      },
    },
    corporateCustomers: {
      info: {
        general: { baseInformation: corporateBaseInformation },
      },
    },
  } = useAppSelector((state) => state);

  const {
    formState: { errors },
    setError,
  } = useForm<Beneficiary>();

  const organizationStatus =
    type === CustomerType.INDIVIDUAL
      ? individualBaseInformation?.status
      : corporateBaseInformation?.status;

  const [getBeneficiary, { isLoading, isFetching }] =
    useLazyGetCustomerBeneficiaryQuery();
  const [updateBeneficiaryStatus, { isLoading: statusInProgress }] =
    useUpdateBeneficiaryStatusMutation();
  const [
    getBeneficiaryDocumentsCategories,
    {
      isLoading: documentsCategoriesIsLoading,
      isFetching: documentsCategoriesIsFetching,
    },
  ] = useLazyGetCustomersBeneficairyDocumentsCategoriesQuery();
  const [
    getBeneficiaryContracts,
    { isLoading: contractsIsLoading, isFetching: contractsIsFetching },
  ] = useLazyGetCustomerBeneficiaryContractsQuery();
  const [
    getBeneficiaryDocumentFile,
    { isLoading: documentIsLoading, isFetching: documentIsFetching, isSuccess },
  ] = useLazyGetBeneficiaryDocumentFileQuery();
  const [validateCustomerBeneficiary] =
    useValidateCustomerBeneficiaryMutation();

  const { filters } = useQueryState(schema);

  const dispatch = useAppDispatch();

  const onSaveStatus = async () => {
    const body: {
      status: BeneficiaryStatus;
      comment?: string;
    } = {
      status: beneficiaryStatus as BeneficiaryStatus,
    };

    if (rejectComment) {
      body.comment = rejectComment;
    }

    const response = (await updateBeneficiaryStatus({
      id: beneficiaryId as string,
      body,
    })) as {
      error: ResponseError;
      data: any;
    };

    if (response.error && response.error.data.message) {
      if (
        Array.isArray(response.error.data.message) &&
        response.error.data.message[0].property === 'coreError'
      ) {
        setUpdateStatusError(response.error.data.message[0].value as string);
      } else if (typeof response.error.data.message === 'string') {
        setUpdateStatusError(response.error.data.message);
      } else {
        setUpdateStatusError('Internal Server Error. Try Again');
      }
    } else if (!response.error && rejectPopupVisible) {
      setRejectPopupVisible(false);
      setRejectComment('');
      setRejectCommentError('');
    }
  };

  const onValidateCustomerBeneficiary = useCallback(async (id: string) => {
    const response = (await validateCustomerBeneficiary(id)) as {
      error?: ResponseError;
    };

    if (response?.error) {
      responseErrorsResolver({
        errors: response.error,
        setError,
        setGlobalError: setGlobalValidateError,
      });
    }
  }, []);

  const onRowClick = async (id: string) => {
    const currentDocument = beneficiary?.documents?.edges?.find(
      (item) => item.node.id === id,
    );

    setSelectedDocumentId(id);

    if (currentDocument) {
      const response = await getBeneficiaryDocumentFile(
        currentDocument.node.uploadId,
      );

      if (response.data) {
        const hiddenElement = document.createElement('a');

        const url = window.URL || window.webkitURL;

        // @ts-ignore
        const blob = url.createObjectURL(response.data);
        hiddenElement.href = blob;
        hiddenElement.target = '_blank';
        hiddenElement.download = currentDocument?.node.name;
        hiddenElement.click();
      }
    }
  };

  useEffect(() => {
    if (beneficiaryId) {
      getBeneficiary(beneficiaryId);
      getBeneficiaryContracts(beneficiaryId);
    }
  }, [beneficiaryId]);

  useEffect(() => {
    if (beneficiary) {
      onValidateCustomerBeneficiary(beneficiary.id);
    }
  }, [beneficiary]);

  const formattedDocumentsData = useMemo(() => {
    const data = formatBeneficiaryDocumentsList(
      beneficiary?.documents?.edges?.map((item) => item.node) as {
        id: string;
        documentCategoryId: string;
        name: string;
        uploadId: string;
        render?: Record<string, React.JSX.Element>;
      }[],
      documentsCategories as { id: string; name: string }[],
      documentIsLoading,
      documentIsFetching,
      selectedDocumentId,
    );

    return data;
  }, [
    beneficiary,
    documentsCategories,
    documentIsLoading,
    documentIsFetching,
    selectedDocumentId,
  ]);

  const formattedData = useMemo(() => {
    const nextBeneficiaryContracts = beneficiaryContracts?.map((item) => ({
      ...item,
      url: generatePath(
        type === CustomerType.INDIVIDUAL
          ? ROUTES.CUSTOMERS_INDIVIDUAL_BENEFICIARY_CONTRACT
          : ROUTES.CUSTOMERS_CORPORATE_BENEFICIARY_CONTRACT,
        {
          customerId: customerId as string,
          beneficiaryId: item.beneficiaryId,
          contractId: item.id,
        },
      ),
    }));

    const data = formatBeneficiaryContractsList(
      nextBeneficiaryContracts as BeneficiaryContract[],
      baseCurrency,
      currenciesList,
    );

    return data;
  }, [beneficiaryContracts, currenciesList, baseCurrency]);

  useEffect(() => {
    if (isSuccess) {
      setSelectedDocumentId('');
    }
  }, [isSuccess]);

  useEffect(() => {
    if (beneficiary) {
      setBeneficiaryStatus(beneficiary.status);

      if (beneficiary?.paymentType === 'CASH_OVER_COUNTER') {
        getBeneficiaryDocumentsCategories();
      }
    }
  }, [beneficiary]);

  let content;

  if (isLoading || isFetching) {
    content = (
      <div className={cn.beneficiaryLoader}>
        <IconLoader />
      </div>
    );
  } else if (beneficiary) {
    const paymentTypeArray = beneficiary.paymentType.split('_');

    const paymentType = paymentTypeArray
      ?.map(
        (typeValue) =>
          `${typeValue.charAt(0)}${typeValue.slice(1).toLocaleLowerCase()}`,
      )
      .join(' ');

    let sortCodeValue;

    if (beneficiary?.sortCode) {
      const regex = /.{1,2}/g;

      sortCodeValue = beneficiary?.sortCode
        ?.replace(/[^0-9]/g, '')
        ?.match(regex)
        ?.join('-');
    }

    content = (
      <>
        <div className={cn.beneficiaryContent}>
          <div className={cn.beneficiaryContentTop}>
            <div className={cn.beneficiaryName}>
              {beneficiary.name}
              {errors?.name && (
                <div className={cn.nameError}>{errors?.name?.message}</div>
              )}
            </div>
            <div className={cn.beneficiarySaveWrapper}>
              {organizationStatus !== CustomerStatus.CLOSED && (
                <div className={cn.saveStatusButtonWrapper}>
                  <ButtonPrimary
                    text={statusInProgress ? 'Loading...' : 'Save Changes'}
                    className={cn.saveStatusButton}
                    disabled={
                      beneficiary.status === beneficiaryStatus ||
                      statusInProgress
                    }
                    onClick={onSaveStatus}
                    bold
                  />
                </div>
              )}

              {updateStatusError && (
                <div className={cn.updateStatusError}>{updateStatusError}</div>
              )}
            </div>
          </div>
          <div className={cn.statusWrapper}>
            <Select
              name="status"
              title="Status"
              value={beneficiaryStatus}
              selectWrapperClassName={cn.beneficiaryStatus}
              list={[
                {
                  name: 'Draft',
                  value: BeneficiaryStatus.DRAFT,
                  hidden: beneficiary.status !== BeneficiaryStatus.DRAFT,
                },
                {
                  name: 'Under Review',
                  value: BeneficiaryStatus.UNDER_REVIEW,
                  hidden: beneficiary.status === BeneficiaryStatus.APPROVED,
                },
                {
                  name: 'Approved',
                  value: BeneficiaryStatus.APPROVED,
                  hidden: beneficiary.status === BeneficiaryStatus.DRAFT,
                },
                {
                  name: 'Rejected',
                  value: BeneficiaryStatus.REJECTED,
                  hidden: beneficiary.status === BeneficiaryStatus.DRAFT,
                },
              ]}
              onChange={(e) => {
                setBeneficiaryStatus(e.target.value as BeneficiaryStatus);

                if (e.target.value === BeneficiaryStatus.REJECTED) {
                  setRejectPopupVisible(true);
                }
              }}
              disabled={
                beneficiary.status === BeneficiaryStatus.REJECTED ||
                organizationStatus === CustomerStatus.CLOSED
              }
            />
            {activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
              <div className={cn.logsButtonWrapper}>
                <NavLink
                  className={cn.logsButton}
                  target="_blank"
                  to={`/administration/change-logs?entityType=BENEFICIARY&entityId=${beneficiary.id}`}
                >
                  <IconChangeLoClock />
                  Change log
                </NavLink>
              </div>
            )}
          </div>
        </div>
        <div className={`${cn.beneficiaryContent} ${cn.moreMargin}`}>
          <div className={cn.beneficiaryContentBlock}>
            <div className={cn.beneficiryContentColumn}>
              <BeneficiaryContentRow
                name="Transfer type"
                value={`${paymentType} ${
                  beneficiary.currencyId
                    ? `(${beneficiary.currency?.name})`
                    : ''
                }`}
              />
              {beneficiary?.paymentType !== 'CASH_OVER_COUNTER' && (
                <BeneficiaryContentRow
                  name="Account Number"
                  value={
                    beneficiary.iban || beneficiary.bankAccountNumber || '-'
                  }
                  error={
                    errors?.iban?.message || errors?.bankAccountNumber?.message
                  }
                />
              )}
              {beneficiary?.aba && (
                <BeneficiaryContentRow
                  name="ABA"
                  value={beneficiary.aba}
                  error={errors?.aba?.message}
                />
              )}
              {beneficiary?.sortCode && (
                <BeneficiaryContentRow
                  name="Sort Code"
                  value={sortCodeValue as string}
                  error={errors?.sortCode?.message}
                />
              )}

              {beneficiary?.paymentType === 'CASH_OVER_COUNTER' && (
                <>
                  <BeneficiaryContentRow
                    name="Collector contact"
                    value={
                      beneficiary?.collectorContact
                        ? formatPhone(beneficiary?.collectorContact)
                        : '-'
                    }
                    error={errors?.collectorContact?.message}
                  />
                  <BeneficiaryContentRow
                    name="Country"
                    value={beneficiary?.country?.name || '-'}
                    error={errors?.countryId?.message}
                  />
                </>
              )}
            </div>
            <div className={cn.beneficiryContentColumn}>
              <BeneficiaryContentRow
                name="Type"
                value={
                  beneficiary.beneficiaryType === 'INDIVIDUAL'
                    ? 'Individual'
                    : 'Company'
                }
                error={errors?.beneficiaryType?.message}
              />
              <BeneficiaryContentRow
                name="Nickname"
                value={beneficiary.nickname}
                error={errors?.nickname?.message}
              />
            </div>
          </div>
          {beneficiary?.paymentType !== 'CASH_OVER_COUNTER' && (
            <>
              <div className={cn.beneficiaryContentBlock}>
                <div className={cn.beneficiryContentColumn}>
                  <BeneficiaryContentRow
                    name="Address"
                    value={beneficiary.address || '-'}
                    error={errors?.address?.message}
                  />
                  <BeneficiaryContentRow
                    name="City"
                    value={beneficiary.city || '-'}
                    error={errors?.city?.message}
                  />
                </div>
                <div className={cn.beneficiryContentColumn}>
                  <BeneficiaryContentRow
                    name="Country"
                    value={beneficiary?.country?.name || '-'}
                    error={errors?.countryId?.message}
                  />
                  <BeneficiaryContentRow
                    name="Post Code"
                    value={beneficiary?.postCode || '-'}
                    error={errors?.postCode?.message}
                  />
                </div>
              </div>
              <div className={cn.beneficiaryContentBlock}>
                <div className={cn.beneficiryContentColumn}>
                  <BeneficiaryContentRow
                    name="Bank Name"
                    value={beneficiary.bankName || '-'}
                    error={errors?.bankName?.message}
                  />
                  <BeneficiaryContentRow
                    name="BIC"
                    value={beneficiary.bic || '-'}
                    error={errors?.bic?.message}
                  />
                </div>
                <div className={cn.beneficiryContentColumn}>
                  <BeneficiaryContentRow
                    name="Bank Country"
                    value={
                      countries?.find(
                        (item) => item.value === beneficiary?.bankCountry,
                      )?.name || '-'
                    }
                    error={errors?.bankCountry?.message}
                  />
                  <BeneficiaryContentRow
                    name="Bank address"
                    value={beneficiary.bankAddress || '-'}
                    error={errors?.bankAddress?.message}
                  />
                </div>
              </div>
            </>
          )}
          {globalValidateError && (
            <div className={cn.globalValidateError}>{globalValidateError}</div>
          )}
        </div>
        {beneficiarySettings?.find(
          (item) => item.key === 'CORPORATE-BENEFICIARIES-CONTRACTS-ENABLED',
        )?.data.value && (
          <div className={cn.contractsWrapper}>
            <div className={cn.contractsTitle}>Contracts</div>
            <Table
              columns={columns}
              data={formattedData || []}
              onSort={() => {}}
              filters={filters}
              sort={{}}
              columnsLabels={columnsLabels}
              isLoading={contractsIsLoading || contractsIsFetching}
              tableClassName={
                !beneficiaryContracts?.length ||
                contractsIsLoading ||
                contractsIsFetching
                  ? cn.contractsEmpty
                  : ''
              }
              tableTopClassName={cn.beneficiaryTableTop}
              onFilterChange={() => {}}
              take="20"
              clickable
            />
          </div>
        )}
        {beneficiary?.paymentType === 'CASH_OVER_COUNTER' && (
          <div className={cn.documentsWrapper}>
            <div className={cn.contractsTitle}>Documents</div>
            <Table
              columns={documentsColumns}
              data={formattedDocumentsData || []}
              onSort={() => {}}
              filters={{}}
              sort={{}}
              columnsLabels={documentsColumnsLabels}
              isLoading={
                documentsCategoriesIsLoading || documentsCategoriesIsFetching
              }
              tableClassName={
                !beneficiary?.documents?.edges?.length ||
                documentsCategoriesIsLoading ||
                documentsCategoriesIsFetching
                  ? cn.contractsEmpty
                  : ''
              }
              onRowClick={onRowClick}
              tableTopClassName={cn.beneficiaryTableTop}
              onFilterChange={() => {}}
            />
            {errors?.documents?.message && (
              <div
                className={`${cn.beneficiaryContentRowError} ${cn.documentsErrors}`}
              >
                {errors?.documents?.message}
              </div>
            )}
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <div className={cn.beneficiaryTop}>
        <NavLink
          to={generatePath(
            type === CustomerType.INDIVIDUAL
              ? ROUTES.CUSTOMERS_INDIVIDUAL_BENEFICIARIES
              : ROUTES.CUSTOMERS_CORPORATE_BENEFICIARIES,
            { customerId: customerId as string },
          )}
          className={cn.backLink}
          onClick={() => dispatch(clearBeneficiaryState())}
        >
          <IconArrowLeft width={13} height={11} />
          Back
        </NavLink>
      </div>
      {content}
      <Popup
        isVisible={rejectPopupVisible}
        onCancelClick={() => {
          setBeneficiaryStatus(beneficiary?.status);
          setRejectPopupVisible(false);
          setRejectComment('');
        }}
        onOkClick={() => {
          if (!rejectComment) {
            setRejectCommentError('Description of the reason is required');
          } else {
            setRejectCommentError('');
            onSaveStatus();
          }
        }}
        isLoading={statusInProgress}
        okButtonText={statusInProgress ? 'Loading...' : 'Save'}
        cancelButtonText="Cancel"
        title="Describe the reason for the rejection"
      >
        <Textarea
          name="rejectComment"
          value={rejectComment}
          onChange={(e) => setRejectComment(e.target.value)}
          placeholder="Describe the reason..."
          error={rejectCommentError}
          labelClassName={cn.rejectCommentTextarea}
        />
      </Popup>
      <Outlet />
    </>
  );
};

export default BeneficiaryPage;
